import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Row, Col, Button, Table, Form, Card } from "react-bootstrap";
import Pagination from "rc-pagination";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import Fancybox from "../../Component/FancyBox";
import Layout from "../../layout/Layout";
import StatusSwitch from "../../Component/theme-management/StatusSwitch";
import TableSkeleton from "../../Component/theme-management/TableSkeleton";
import EmptyState from "../../Component/theme-management/EmptyState";
import ErrorState from "../../Component/theme-management/ErrorState";
import ImageWithFallback from "../../Component/theme-management/ImageWithFallback";
import { themesApi, isAuthError } from "../../helpers/tmClient";
import {
  THEME_TYPE_OPTIONS,
  PAGE_SIZE_OPTIONS,
  labelOf,
} from "../../helpers/tmConstants";

/**
 * Light-background Type badges (dark text, rounded) — consistent with
 * the rest of the admin panel. Home → light blue, Lock → light purple.
 */
const THEME_TYPE_BADGE_STYLE = {
  home: { backgroundColor: "#e7f1ff", color: "#0b5ed7" },
  lock: { backgroundColor: "#efe7fb", color: "#6f42c1" },
  default: { backgroundColor: "#e9ecef", color: "#495057" },
};

/**
 * Themes — listing page (Theme Management module).
 * Server-side pagination + debounced search + Home/Lock, Premium,
 * Status and Category filters. Independent of legacy theme pages.
 */
const Themes = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [meta, setMeta] = useState({ page: 1, limit: 10, total: 0, totalPages: 1 });
  const [filters, setFilters] = useState({
    search: "",
    theme_type: "",
    premium: "",
    status: "",
    category: "",
  });
  const debounceRef = useRef(null);
  const lastArgsRef = useRef({});
  // Per-row "updating" flags — any in-flight action (status / premium /
  // delete) disables that row's controls so it can't be double-triggered.
  const [rowBusy, setRowBusy] = useState({});

  const fetchData = useCallback(
    async (overrides = {}) => {
      lastArgsRef.current = overrides;
      setLoading(true);
      setLoadError(false);
      try {
        const params = { page: meta.page, limit: meta.limit, ...filters, ...overrides };
        const res = await themesApi.list(params);
        if (isAuthError(res)) return navigate("/");
        setData(res.data.data || []);
        if (res.data.meta) setMeta((m) => ({ ...m, ...res.data.meta }));
      } catch (e) {
        setLoadError(true);
        toast.error("Failed to load themes", { position: "bottom-right", autoClose: 1200 });
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [meta.page, meta.limit, filters, navigate]
  );

  const retry = () => fetchData(lastArgsRef.current);

  // Reload on page/limit change.
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta.page, meta.limit]);

  // Debounced reload on filter change (resets to page 1).
  const onFilter = (e) => {
    const { name, value } = e.target;
    const next = { ...filters, [name]: value };
    setFilters(next);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setMeta((m) => ({ ...m, page: 1 }));
      fetchData({ ...next, page: 1 });
    }, 400);
  };

  /**
   * Inline toggle for the Status / Premium switches. Updates the row via
   * the relevant API (no page refresh), shows a per-row loading state for
   * Premium, and surfaces a success/error toast.
   */
  const toggle = async (id, field, value) => {
    setRowBusy((b) => ({ ...b, [id]: true }));
    try {
      if (field === "status") await themesApi.changeStatus(id, value);
      else if (field === "premium") await themesApi.changePremium(id, value);
      setData((d) => d.map((row) => (row._id === id ? { ...row, [field]: value } : row)));
      toast.success(field === "premium" ? "Premium updated" : "Status updated", {
        position: "bottom-right",
        autoClose: 500,
      });
    } catch {
      toast.error("Update failed", { position: "bottom-right", autoClose: 800 });
    } finally {
      setRowBusy((b) => ({ ...b, [id]: false }));
    }
  };

  const remove = (id) => {
    const swal = Swal.mixin({
      customClass: { confirmButton: "btn btn-success", cancelButton: "btn btn-danger me-3" },
      buttonsStyling: false,
    });
    swal
      .fire({
        title: "Are you sure?",
        text: "Deleting a theme also removes its icons, widgets & variants!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, delete it!",
        cancelButtonText: "No, cancel!",
        reverseButtons: true,
      })
      .then(async (result) => {
        if (result.isConfirmed) {
          setRowBusy((b) => ({ ...b, [id]: true }));
          try {
            await themesApi.remove(id);
            toast.success("Theme deleted", { position: "bottom-right", autoClose: 800 });
            fetchData();
          } catch {
            toast.error("Delete failed", { position: "bottom-right", autoClose: 800 });
            setRowBusy((b) => ({ ...b, [id]: false }));
          }
        }
      });
  };

  const PrevNextArrow = (current, type, el) => {
    if (type === "prev") return <button className="paggination-btn">Previous</button>;
    if (type === "next") return <button className="paggination-btn">Next</button>;
    return el;
  };

  /**
   * Drag & drop reorder. Sort order is recalculated for the affected
   * rows using their absolute position (offset by the current page),
   * persisted via the dedicated sort API, then a success toast shows.
   */
  const onDragEnd = async (result) => {
    if (!result.destination || result.destination.index === result.source.index) return;
    const reordered = Array.from(data);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    setData(reordered); // optimistic
    const offset = (meta.page - 1) * meta.limit;
    const items = reordered.map((row, idx) => ({ id: row._id, sort_order: offset + idx + 1 }));
    try {
      await themesApi.updateSort(items);
      toast.success("Order updated", { position: "bottom-right", autoClose: 700 });
      fetchData();
    } catch {
      toast.error("Failed to save order", { position: "bottom-right", autoClose: 900 });
      fetchData();
    }
  };

  return (
    <Layout sidebar={true}>
      <div className="page-heading">
        <h3 className="my-1">Themes</h3>
        <Row className="align-items-center">
          <Col md="auto" xs={12}>
            <Form.Control
              type="text"
              name="search"
              placeholder="Search name / category"
              className="my-1"
              value={filters.search}
              onChange={onFilter}
            />
          </Col>
          <Col md="auto" xs={12}>
            <Form.Select className="my-1" name="theme_type" onChange={onFilter}>
              <option value="">All Types</option>
              {THEME_TYPE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </Form.Select>
          </Col>
          <Col md="auto" xs={12}>
            <Form.Select className="my-1" name="premium" onChange={onFilter}>
              <option value="">All (Premium)</option>
              <option value="1">Premium</option>
              <option value="0">Free</option>
            </Form.Select>
          </Col>
          <Col md="auto" xs={12}>
            <Form.Select className="my-1" name="status" onChange={onFilter}>
              <option value="">All Status</option>
              <option value="1">Active</option>
              <option value="0">Inactive</option>
            </Form.Select>
          </Col>
          <Col md="auto" xs={6}>
            <Form.Select
              className="my-1"
              value={meta.limit}
              onChange={(e) => setMeta((m) => ({ ...m, page: 1, limit: Number(e.target.value) }))}
            >
              {PAGE_SIZE_OPTIONS.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </Form.Select>
          </Col>
          <Col md="auto" xs={12}>
            <Link to="/themes/add">
              <Button variant="primary" className="my-1">Add New</Button>
            </Link>
          </Col>
        </Row>
      </div>

      <div className="page-content">
        <Row>
          <Col xs={12}>
            <Card>
              <Card.Body>
                <DragDropContext onDragEnd={onDragEnd}>
                <Table bordered responsive>
                  <thead>
                    <tr>
                      <th width="5%" className="text-center">No</th>
                      <th width="10%" className="text-center">Thumbnail</th>
                      <th>Name</th>
                      <th>Category</th>
                      <th className="text-center">Type</th>
                      <th className="text-center">Status</th>
                      <th className="text-center">Premium</th>
                      <th className="text-center">Icons</th>
                      <th className="text-center">Widgets</th>
                      <th className="text-center">Wallpapers</th>
                      <th className="text-center">Actions</th>
                    </tr>
                  </thead>
                  {loading ? (
                    <tbody><TableSkeleton rows={6} cols={12} /></tbody>
                  ) : loadError ? (
                    <tbody><ErrorState colSpan={12} message="Failed to load themes." onRetry={retry} /></tbody>
                  ) : data.length === 0 ? (
                    <tbody><EmptyState colSpan={12} message="No themes found" icon="bxs-palette" /></tbody>
                  ) : (
                    <Droppable droppableId="themes-droppable">
                      {(provider) => (
                        <tbody ref={provider.innerRef} {...provider.droppableProps}>
                          {data.map((val, id) => (
                            <Draggable key={val._id} draggableId={val._id} index={id}>
                              {(prov) => (
                        <tr key={val._id} ref={prov.innerRef} {...prov.draggableProps}>
                          <td className="text-center">
                            <i className="bx bx-menu me-1 text-muted" title="Drag to reorder" style={{ cursor: "grab" }} {...prov.dragHandleProps}></i>
                            {(meta.page - 1) * meta.limit + id + 1}
                          </td>
                          <td className="text-center">
                            {val.thumbnail ? (
                              <Fancybox>
                                <a data-fancybox="gallery" href={val.thumbnail}>
                                  <ImageWithFallback src={val.thumbnail} alt="thumb" />
                                </a>
                              </Fancybox>
                            ) : (
                              <ImageWithFallback src={null} alt="thumb" />
                            )}
                          </td>
                          <td>{val.name}</td>
                          <td>{val.category || "-"}</td>
                          <td className="text-center text-capitalize">
                            <span
                              className="badge rounded-pill"
                              style={THEME_TYPE_BADGE_STYLE[val.theme_type] || THEME_TYPE_BADGE_STYLE.default}
                            >
                              {labelOf(THEME_TYPE_OPTIONS, val.theme_type)}
                            </span>
                          </td>
                          <td className="text-center">
                            <StatusSwitch
                              checked={val.status === true}
                              disabled={!!rowBusy[val._id]}
                              onChange={(e) => toggle(val._id, "status", e)}
                            />
                          </td>
                          <td className="text-center">
                            <StatusSwitch
                              checked={val.premium === true}
                              disabled={!!rowBusy[val._id]}
                              onColor="#8b5cf6"
                              onLabel="YES"
                              offLabel="NO"
                              onChange={(e) => toggle(val._id, "premium", e)}
                            />
                          </td>
                          <td className="text-center">{val.icons_count || 0}</td>
                          <td className="text-center">{val.widgets_count || 0}</td>
                          <td className="text-center">{val.wallpapers_count || 0}</td>
                          <td className="text-center" style={{ whiteSpace: "nowrap" }}>
                            <Link to={`/themes/view/${val._id}`}>
                              <Button variant="outline-warning" size="sm" className="me-2 btn-icon" disabled={!!rowBusy[val._id]}><i className="bx bx-show"></i></Button>
                            </Link>
                            <Link to={`/themes/edit/${val._id}`}>
                              <Button variant="outline-primary" size="sm" className="me-2 btn-icon" disabled={!!rowBusy[val._id]}><i className="bx bx-edit"></i></Button>
                            </Link>
                            <Button variant="outline-danger" size="sm" className="btn-icon" disabled={!!rowBusy[val._id]} onClick={() => remove(val._id)}>
                              {rowBusy[val._id] ? (
                                <span className="spinner-border spinner-border-sm" role="status"></span>
                              ) : (
                                <i className="bx bx-trash-alt"></i>
                              )}
                            </Button>
                          </td>
                        </tr>
                              )}
                            </Draggable>
                          ))}
                          {provider.placeholder}
                        </tbody>
                      )}
                    </Droppable>
                  )}
                </Table>
                </DragDropContext>

                {meta.total > meta.limit && (
                  <div
                    className="pagination-custom"
                    style={loading ? { opacity: 0.5, pointerEvents: "none" } : undefined}
                  >
                    <Pagination
                      className="pagination-data"
                      onChange={(page) => setMeta((m) => ({ ...m, page }))}
                      total={meta.total}
                      current={meta.page}
                      pageSize={meta.limit}
                      showSizeChanger={false}
                      itemRender={PrevNextArrow}
                      showTitle={false}
                    />
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    </Layout>
  );
};

export default Themes;
